import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/components/podkit/lib/cn";
import {
    getEnvironmentClassStatus,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import { memo, type FC, type ReactNode } from "react";
import { type LucideIcon, GripVertical, X } from "lucide-react";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Button } from "@/components/flexkit/Button";

type EnvironmentClassStatusProps = {
    offline: boolean;
    policyRestricted: boolean;
    degraded: boolean;
};

const EnvironmentClassStatus: FC<EnvironmentClassStatusProps> = ({ offline, policyRestricted, degraded }) => {
    let message: string | undefined = undefined;
    if (offline) {
        message = "Offline";
    } else if (policyRestricted) {
        message = "Policy restricted";
    } else if (degraded) {
        message = "Degraded";
    }

    if (!message) {
        return null;
    }
    return <span className="text-right text-sm text-content-tertiary">{message}</span>;
};

type EnvironmentClassItemProps = {
    id: string;
    environmentClassEntry: EnvironmentClassEntry;
    disabled?: boolean;
    isDraggable?: boolean;
    icon?: LucideIcon;
    className?: string;
    onRemove?: () => void;
    showPrebuildCheckbox?: boolean;
    isPrebuildSelected?: boolean;
    onPrebuildToggle?: (checked: boolean) => void;
    /** Rendered in the warm pool column cell */
    warmPoolCell?: ReactNode;
    /** Rendered in the prebuild status column cell */
    prebuildStatusCell?: ReactNode;
};

export const EnvironmentClassItem: FC<EnvironmentClassItemProps> = memo(
    ({
        id,
        environmentClassEntry,
        disabled,
        isDraggable,
        icon: Icon,
        className,
        onRemove,
        showPrebuildCheckbox,
        isPrebuildSelected,
        onPrebuildToggle,
        warmPoolCell,
        prebuildStatusCell,
    }) => {
        const sortable = useSortable({
            id,
            disabled: !isDraggable || disabled,
        });

        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

        const style = isDraggable
            ? {
                  transform: CSS.Transform.toString(transform),
                  transition,
              }
            : {};

        const { policyRestricted, offline, degraded } = getEnvironmentClassStatus(environmentClassEntry);

        const hasExtraColumns = showPrebuildCheckbox || warmPoolCell !== undefined || prebuildStatusCell !== undefined;

        return (
            <div ref={setNodeRef} style={style} className={cn("flex w-full min-w-0 flex-col", className)}>
                <div
                    className={cn(
                        "flex w-full min-w-0 flex-col border-border-light px-4 py-2 transition-colors lg:flex-row lg:items-center",
                        isDraggable && !disabled && "cursor-grab hover:bg-surface-hover active:cursor-grabbing",
                        isDragging && "opacity-50",
                        disabled && "cursor-not-allowed opacity-50",
                    )}
                    {...(isDraggable && !disabled ? attributes : {})}
                    {...(isDraggable && !disabled ? listeners : {})}
                >
                    {/* Row 1: drag handle + name + remove (always) */}
                    <div className="flex w-full min-w-0 flex-1 items-center">
                        {isDraggable && (
                            <div className="flex items-center justify-center pr-2 text-content-secondary">
                                <GripVertical size={16} />
                            </div>
                        )}

                        <div className="flex w-full min-w-0 flex-1 items-center justify-between gap-2">
                            <div className="flex min-w-0 flex-1 flex-col items-start">
                                <div className="flex w-full min-w-0 gap-1">
                                    <span
                                        title={environmentClassEntry?.clazz?.displayName}
                                        className="min-w-0 shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-content-primary group-data-[disabled]:text-content-tertiary lg:shrink"
                                    >
                                        {environmentClassEntry?.clazz?.displayName}
                                    </span>
                                    <span
                                        className="min-w-0 shrink overflow-hidden text-ellipsis whitespace-nowrap text-base text-content-tertiary"
                                        title={environmentClassEntry?.runner?.name}
                                    >
                                        {environmentClassEntry?.runner?.name}
                                    </span>
                                </div>
                                <div className="flex w-full gap-1">
                                    <span
                                        className="overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm text-content-tertiary"
                                        title={environmentClassEntry?.clazz?.description}
                                    >
                                        {environmentClassEntry?.clazz?.description}
                                    </span>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <EnvironmentClassStatus
                                    offline={offline}
                                    policyRestricted={policyRestricted}
                                    degraded={degraded}
                                />
                                {Icon && (
                                    <div aria-hidden="true" className="flex h-4 w-4 items-center justify-center text-content-secondary">
                                        <Icon size={16} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Remove button: inline on lg+, end of row 1 on small */}
                        {onRemove && (
                            <div className="flex items-center justify-center pl-2 lg:hidden">
                                <Button
                                    variant="ghost"
                                    disabled={disabled}
                                    onClick={onRemove}
                                    aria-label="Remove environment class"
                                    data-tracking-id="remove-environment-class-mobile"
                                    className="size-6 p-0"
                                >
                                    <X size={14} />
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Row 2 (small) / inline columns (lg+): prebuild + warm pool + status */}
                    {hasExtraColumns && (
                        <div className="mt-1.5 flex flex-col gap-1 pl-6 lg:mt-0 lg:flex-row lg:items-center lg:gap-0 lg:pl-0">
                            {/* Labels row — small screens only.
                                Warm pool and prebuild status labels are hidden when prebuild is
                                not selected since their cells only show "--" placeholders. */}
                            <div className="flex gap-3 lg:hidden">
                                {showPrebuildCheckbox && (
                                    <span className="w-10 text-center text-xs text-content-tertiary">Prebuild</span>
                                )}
                                {warmPoolCell !== undefined && isPrebuildSelected && (
                                    <span className="w-[80px] text-xs text-content-tertiary">Warm pool</span>
                                )}
                                {prebuildStatusCell !== undefined && isPrebuildSelected && (
                                    <span className="text-xs text-content-tertiary">Prebuild status</span>
                                )}
                            </div>
                            {/* Controls row */}
                            <div className="flex items-center gap-3 lg:gap-0">
                                {showPrebuildCheckbox && (
                                    <div className="flex w-10 justify-center lg:w-[72px]">
                                        <Checkbox
                                            checked={isPrebuildSelected || false}
                                            onCheckedChange={(checked) => onPrebuildToggle?.(checked === true)}
                                            disabled={disabled}
                                            aria-label="Enable prebuild for this environment class"
                                        />
                                    </div>
                                )}

                                {warmPoolCell !== undefined && (
                                    <div className={cn("w-[80px] lg:flex lg:w-[140px] lg:justify-center", isPrebuildSelected ? "flex" : "hidden")}>
                                        {warmPoolCell}
                                    </div>
                                )}

                                {prebuildStatusCell !== undefined && (
                                    <div className={cn("items-center lg:flex lg:w-[120px] lg:justify-center", isPrebuildSelected ? "flex" : "hidden")}>
                                        {prebuildStatusCell}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Remove button: only visible on lg+ as part of the single row */}
                    {onRemove && (
                        <div className="hidden items-center justify-center pl-2 lg:flex">
                            <Button
                                variant="ghost"
                                disabled={disabled}
                                onClick={onRemove}
                                aria-label="Remove environment class"
                                data-tracking-id="remove-environment-class"
                                className="size-6 p-0"
                            >
                                <X size={14} />
                            </Button>
                        </div>
                    )}
                </div>

            </div>
        );
    },
);

EnvironmentClassItem.displayName = "EnvironmentClassItem";
